import {
	keepPreviousData,
	QueryKey,
	useQueries,
	useQuery,
} from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { getPhases } from "../services/api-server/phases";
import { useTenantStore } from "../stores/tenantStore";
import { isValidObjectId } from "../utils/find_unique_ids_in_list";
import {
	combineIdMaps,
	groupObjectIdsByOperator,
	toIdMap,
} from "../utils/reporting/reportingHubFilters";
import { fetchEmailToNameMap } from "../utils/responsibleNames";

// Until the people are looked up, nobody has a name.
const NO_NAMES: Record<string, string> = {};

// The names of the people a stream lists, by lower-cased email, looked up
// for those emails alone. queryKey starts the lookup's key, which ends with
// the emails.
export function useReportStreamNames(queryKey: QueryKey, emails: string[]) {
	const { data = NO_NAMES } = useQuery({
		queryKey: [...queryKey, emails],
		queryFn: () => fetchEmailToNameMap(emails),
		enabled: emails.length > 0,
		staleTime: 5 * 60 * 1000,
		// A new person changes the key; the names so far stay meanwhile.
		placeholderData: keepPreviousData,
	});
	return data;
}

type OperatorRow = { operator?: string };

// The records the rows name by id in one field, such as a report's phase, by
// id: looked up in the rows' tenants, as the filters look up wells. Only
// ObjectId text goes into a lookup, and only a tenant with ids gets one. The
// map keeps its identity until the records change. It is partial while the
// lookups load, or when one fails, such as for a tenant the user can't read.
export function useOperatorRecords<T extends OperatorRow>(
	rows: readonly T[],
	field: keyof T & string,
	queryKey: string,
	fetchRecords: (operator: string, ids: string[]) => Promise<unknown>,
	currentTenantKey: string | undefined
): Record<string, any> {
	const idsByOperator = useMemo(
		() => groupObjectIdsByOperator(rows, field),
		[rows, field]
	);

	return useQueries({
		queries: [...idsByOperator].map(([operator, ids]) => ({
			queryKey: [queryKey, operator, ids.join(",")],
			queryFn: () => fetchRecords(operator, ids),
			enabled: !!currentTenantKey,
			staleTime: 5 * 60 * 1000,
		})),
		combine: combineIdMaps,
	}).map;
}

// Until the records are looked up, none is known.
const NO_RECORDS: Record<string, any> = {};

// The records the rows name by id in one field from a list every tenant
// shares, such as a lesson's discipline, by id: one lookup of the listed ids,
// and none without ids. Only ObjectId text goes into it. The map keeps its
// identity until the records change; it is empty until they load, or after
// a failure.
export function useSharedRecords<T>(
	rows: readonly T[],
	field: keyof T & string,
	queryKey: string,
	fetchRecords: (ids: string[]) => Promise<unknown>
): Record<string, any> {
	const ids = useMemo(() => {
		const values = new Set<string>();
		for (const row of rows) {
			const id = row[field];
			if (isValidObjectId(id)) values.add(id as string);
		}
		return [...values].sort((a, b) => a.localeCompare(b));
	}, [rows, field]);

	const { data } = useQuery({
		queryKey: [queryKey, ids.join(",")],
		queryFn: () => fetchRecords(ids),
		enabled: ids.length > 0,
		staleTime: 5 * 60 * 1000,
		// A new id changes the key; the names so far stay meanwhile.
		placeholderData: keepPreviousData,
	});
	return useMemo(
		() => (Array.isArray(data) ? toIdMap(data) : NO_RECORDS),
		[data]
	);
}

const fetchPhases = (operator: string, ids: string[]) =>
	getPhases(operator, { _id: { $in: ids } });

// The phases the rows name, by id (useOperatorRecords).
export const useReportPhases = <T extends OperatorRow & { phase?: unknown }>(
	rows: readonly T[],
	currentTenantKey: string | undefined
) =>
	useOperatorRecords(
		rows,
		"phase",
		"report-stream-phases",
		fetchPhases,
		currentTenantKey
	);

// A tenant's name by its key, from the user's tenants, for a stream's rows
// and search.
export function useTenantName() {
	const tenantList = useTenantStore((state) => state.tenantList);
	return useCallback(
		(tenantKey: string): string | undefined => {
			const tenant = tenantList.find(
				(item: any) => item.role_key === tenantKey
			);
			return tenant?.name || tenant?.displayName; // NOSONAR: an empty name falls back to the display name
		},
		[tenantList]
	);
}
